import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { GlobalKeyEnum } from '../models/i18n.model';
import { LinkOrderOffer } from '../models/sip-order-link.model';
import { SipOrderLinkService } from './sip-order-link.service';
import { LegalLinksService } from './legal-links.service';
import { FeatureEnum } from '../models/feature.model';
import {
	CallbackOnCallNote,
	CallbackOnCallNoteWorkflow,
	Configuration,
	CONFIGURATIONS,
	ConfigurationTypeEnum,
} from '../models/constant.model';
import { CallbackOnCallNoteDTO } from '../models/callback-on-callnote.model';
import { FeedbackCallnoteDTO, FeedbackWorkflow } from '../models/feedback.model';
import { DataService } from './data.service';
import { ParamsEnum } from '../models/params.model';
import { LocalizationService } from './localization.service';

@Injectable({
	providedIn: 'root',
})
export class FeatureService {
	private _features$ = new ReplaySubject<FeatureEnum[]>(1);
	private _callbackOnCallNoteData$ = new ReplaySubject<CallbackOnCallNote>(1);
	private _feedbackData$ = new ReplaySubject<FeedbackCallnoteDTO>(1);
	private activeConfiguration?: Configuration;
	public features$ = this._features$.asObservable();
	public callbackOnCallNoteData$ = this._callbackOnCallNoteData$.asObservable();
	public feedbackData$ = this._feedbackData$.asObservable();
	private _sipOrderOffer$ = new ReplaySubject<LinkOrderOffer>(1);
	/** The SIP order link's offer, emitted once its translations and legal links are fed in. */
	public sipOrderOffer$ = this._sipOrderOffer$.asObservable();
	public errorDetails: string | null = null;
	public errorKind: 'expired' | 'generic' = 'generic';
	public errorUserMessage: string | null = null;

	constructor(
		private readonly dataService: DataService,
		private readonly localizationService: LocalizationService,
		private readonly sipOrderLink: SipOrderLinkService,
		private readonly legalLinks: LegalLinksService,
	) {
		this.init();
	}

	private init() {
		const params = new URLSearchParams(location.search);
		// TODO - add error handling - no params
		this.setConfiguration(params);
	}

	private setConfiguration(params: URLSearchParams) {
		// TODO first - token check

		for (const config of CONFIGURATIONS) {
			let identifiedConfiguration = true;
			for (const param of config.params.required) {
				if (!params.has(param)) {
					identifiedConfiguration = false;
					break;
				}
			}
			if (identifiedConfiguration) {
				this.activeConfiguration = config;

				if (config.type === ConfigurationTypeEnum.SIP_ORDER) {
					this.startSipOrder();
				} else if (config.type === ConfigurationTypeEnum.FEEDBACK) {
					const uid = params.get(ParamsEnum.UID)?.trim();
					if (uid) {
						const ratingParam = Number(params.get(ParamsEnum.RATING));
						const feedbackWorkflow: FeedbackWorkflow = {
							uid,
							rating: Number.isFinite(ratingParam) ? ratingParam : 0,
						};
						this.fetchFeedbackData(feedbackWorkflow);
					}
				} else if (config.type === ConfigurationTypeEnum.CALLBACK_ON_CALLNOTE) {
					const uid = params.get(ParamsEnum.UID)?.trim();
					if (uid) {
						const callbackOnCallNoteWorkflow: CallbackOnCallNoteWorkflow = {
							uid,
							priceInfo: !!params.get(ParamsEnum.SHOW_PRICE_INFO),
							suggestion: params.get(ParamsEnum.SUGGESTION),
						};
						this.fetchData(callbackOnCallNoteWorkflow);
					}
				} else if (config.type === ConfigurationTypeEnum.CALLNOTE_INFO) {
					const uid = params.get(ParamsEnum.UID)?.trim();
					if (uid) {
						const feedbackWorkflow: FeedbackWorkflow = { uid, rating: 0 };
						this.fetchFeedbackData(feedbackWorkflow);
					}
				}
				break;
			}
		}

		if (!this.activeConfiguration) {
			this.errorDetails = 'No configuration found. Missing required URL parameter: uid';
			console.error(this.errorDetails);
			this._features$.next([FeatureEnum.ERROR]);
		}
	}

	/** Loads the SIP order link's offer; any failure ends on the error page. */
	private startSipOrder(): void {
		const token = this.sipOrderLink.captureFromUrl();
		if (token === null) {
			this.showConfigurationError('SIP order link without a uid');
			return;
		}
		if (this.activeConfiguration) {
			this._features$.next(this.activeConfiguration.features);
		}
		this.sipOrderLink.loadOffer$().subscribe({
			next: (offer) => {
				// Without texts the wizard would render an empty card.
				if (!offer?.translations?.[GlobalKeyEnum.CONNECT]) {
					this.sipOrderLink.clear();
					this.showConfigurationError(`SIP order link offer without texts, status ${offer?.status}`);
					return;
				}
				this.localizationService.setTranslations(offer.translations);
				this.legalLinks.setFromTree(offer.legalLinks);
				this._sipOrderOffer$.next(offer);
			},
			error: (err) => {
				this.sipOrderLink.clear();
				// Not `handleFetchError`: it would show the backend's internal message to the customer.
				this.showConfigurationError(`SIP order link offer failed: HTTP ${err?.status ?? 'unknown'}`);
			},
		});
	}

	/** The generic error page for a link this page cannot serve; never carries the uid. */
	private showConfigurationError(details: string): void {
		this.errorKind = 'generic';
		this.errorUserMessage = null;
		this.errorDetails = details;
		console.error(this.errorDetails);
		this._features$.next([FeatureEnum.ERROR]);
	}

	private fetchData(wfData: CallbackOnCallNoteWorkflow) {
		this.dataService.fetchCallbackOnCallNote$(wfData).subscribe({
			next: (data) => {
				if (this.activeConfiguration) {
					this._features$.next(this.activeConfiguration.features);
				}
				this.localizationService.setTranslations(data.translations);
				this._callbackOnCallNoteData$.next(new CallbackOnCallNote(data, wfData));
			},
			error: (err) => {
				this.handleFetchError(err, 'API request failed');
			},
		});
	}

	private fetchFeedbackData(wfData: FeedbackWorkflow) {
		this.dataService.fetchFeedback$(wfData).subscribe({
			next: (data) => {
				if (this.activeConfiguration) {
					this._features$.next(this.activeConfiguration.features);
				}
				this.localizationService.setTranslations(data.translations);
				this._feedbackData$.next(data);

				// Construct compatible CallbackOnCallNoteDTO wrapper so Note component works unchanged
				const compatibleDTO: CallbackOnCallNoteDTO = {
					languageId: data.languageId,
					timeZone: data.timeZone,
					callNote: data.callNote,
					callReasonId: null,
					instructionSuggestions: [],
					existingOutboundTask: null,
					businessHours: [],
					outboundOutgoingPhoneNumber: null,
					translations: data.translations,
				};
				const compatibleWorkflow: CallbackOnCallNoteWorkflow = {
					uid: wfData.uid,
					suggestion: null,
					priceInfo: null,
				};
				this._callbackOnCallNoteData$.next(new CallbackOnCallNote(compatibleDTO, compatibleWorkflow));
			},
			error: (err) => {
				this.handleFetchError(err, 'Feedback API request failed');
			},
		});
	}

	private handleFetchError(err: any, prefix: string) {
		const backendMessage = this.extractBackendMessage(err);
		if (err?.status === 410) {
			this.errorKind = 'expired';
			this.errorUserMessage = backendMessage;
		} else {
			this.errorKind = 'generic';
			this.errorUserMessage = backendMessage;
		}
		this.errorDetails = `${prefix}: ${backendMessage || err?.message || err?.status || 'Unknown error'}`;
		console.error(this.errorDetails, err);
		this._features$.next([FeatureEnum.ERROR]);
	}

	private extractBackendMessage(err: any): string | null {
		const body = err?.error;
		if (!body) return null;
		if (typeof body === 'string') return body;
		return body.message || body.detail || body.error || null;
	}
}
